import { observer } from 'mobx-react-lite'
import { type ChangeEvent, useRef } from 'react'
import classNames from 'classnames'
import { Button, Dropdown, DropdownCard, DropdownContent } from 'shared/ui'
import { LINK_KNOWLEDGE_BASE_HELP_GUIDE } from 'shared/constants/links'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { KnowledgeBasePath } from 'entities/ExportRouters'
import { useKnowledgeBaseViewContext } from 'pages/settings/pages/knowledgeBase/context/knowledgeBaseViewContext'
import { KnowledgeBaseActionsUrlInput } from './KnowledgeBaseActionsUrlInput'
import styles from './styles.module.scss'

export const KnowledgeBaseActions = observer(() => {
  const { attachmentStore } = useEntitiesContext()
  const {
    documentsStore: { isEmpty, onUploadDocuments, urlStore },
    isUploadDisabled,
  } = useKnowledgeBaseViewContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    url,
    setUrl,
    isUploading,
    handleImportUrl,
    handleImportGoogleLink,
    isUploadViaUrlOpen,
    setIsUploadViaUrlOpen,
    isUploadViaGoogleOpen,
    setIsUploadViaGoogleOpen,
    errorMessages,
  } = urlStore

  const isInputOpen = isUploadViaUrlOpen || isUploadViaGoogleOpen

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.click()
    }
  }

  const handleCreateDocument = () => {
    uiStore.changeRouteViaVue({
      path: KnowledgeBasePath.createDocument,
    })
  }

  const onInputFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files

    if (files) {
      void onUploadDocuments(Array.from(files))
    }
  }

  return (
    <div
      className={classNames(styles.actionsContainer, {
        [styles.emptyView]: isEmpty,
      })}
    >
      {!isInputOpen && (
        <>
          <Button
            tag='a'
            text='Help guide'
            size='medium'
            contained='secondary'
            iconProps={{ icon: 'info' }}
            className={classNames(styles.helpButton, {
              [styles.emptyView]: isEmpty,
            })}
            href={LINK_KNOWLEDGE_BASE_HELP_GUIDE}
            target='_blank'
            rel='noreferrer'
          />
          <input
            type={'file'}
            className={styles.fileInput}
            ref={inputRef}
            accept={attachmentStore.extendedFilesAcceptString}
            disabled={isUploadDisabled}
            multiple
            onChange={onInputFileChange}
          />
          <Dropdown
            triggerComponent={() => (
              <Button text='Add knowledge' iconProps={{ icon: 'add' }} size='medium' />
            )}
            placement={'bottom-end'}
            customComponent={(onCloseMenu) => (
              <DropdownContent width={194}>
                <DropdownCard
                  item={{
                    id: 'upload',
                    label: 'Upload or drop',
                    iconL: 'upload',
                    ariaLabel: 'uploadSelector',
                  }}
                  onChange={() => {
                    onCloseMenu()
                    handleUploadClick()
                  }}
                />
                <DropdownCard
                  item={{
                    id: 'google',
                    label: 'Add Google link',
                    iconL: 'link',
                    ariaLabel: 'googleLinkSelector',
                  }}
                  onChange={() => {
                    onCloseMenu()
                    setIsUploadViaGoogleOpen(true)
                  }}
                />
                <DropdownCard
                  item={{
                    id: 'url',
                    label: 'Import web page',
                    iconL: 'globe',
                    ariaLabel: 'urlSelector',
                  }}
                  onChange={() => {
                    onCloseMenu()
                    setIsUploadViaUrlOpen(true)
                  }}
                />
                <DropdownCard
                  item={{
                    id: 'block',
                    label: 'Create document',
                    iconL: 'edit',
                    ariaLabel: 'createDocumentSelector',
                  }}
                  onChange={() => {
                    onCloseMenu()
                    handleCreateDocument()
                  }}
                />
              </DropdownContent>
            )}
          />
        </>
      )}

      {isUploadViaUrlOpen && (
        <KnowledgeBaseActionsUrlInput
          placeholder='https://website.com'
          submitLabel='Import'
          value={url}
          isUploading={isUploading}
          error={errorMessages.url}
          onChange={setUrl}
          onSubmit={handleImportUrl}
          onCancel={() => setIsUploadViaUrlOpen(false)}
        />
      )}

      {isUploadViaGoogleOpen && (
        <KnowledgeBaseActionsUrlInput
          placeholder='https://docs.google.com'
          submitLabel='Next'
          value={url}
          isUploading={isUploading}
          error={errorMessages.url}
          onChange={setUrl}
          onSubmit={handleImportGoogleLink}
          onCancel={() => setIsUploadViaGoogleOpen(false)}
        />
      )}
    </div>
  )
})
